
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import { NotificationOptions } from 'vue-notification';
import { NavBar, BaseModal } from '@warrenbrasil/nebraska-web';
import { Portal } from '@/modules/common/components/Portal';
import { CloseButton } from '@/modules/common/components/buttons';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { Notification } from '@/modules/common/components/Notification';
import { SuggestionType } from '@/modules/investments/types/suggestion';
import PortfolioSuggestedList from '../PortfolioSuggestedList/PortfolioSuggestedList.vue';
import PortfolioSuggestedDetails from '../PortfolioSuggestedDetails/PortfolioSuggestedDetails.vue';
import InvestmentSuggestedDetails from '../InvestmentSuggestedDetails/InvestmentSuggestedDetails.vue';
import SuggestedValidationError from '../SuggestedValidationError/SuggestedValidationError.vue';
import PortfolioSuggestedRefuse from '../PortfolioSuggestedRefuse/PortfolioSuggestedRefuse.vue';
import { namespace } from 'vuex-class';
import { ICustomer } from '@/types/models';
import { SuggestionModule } from '@/modules/home/store/suggestions';
import {
  toggleBodyScroll,
  addBodyScroll
} from '@/modules/common/helpers/browser';
import {
  trackingCloseBtnStepDetail,
  trackingBackBtnStepDetail,
  closeButtonWalletDetail
} from './track-investment-events';
import { trackViewProductDetails } from './track-redeem-events';

const CustomerModule = namespace('CustomerModule');

export enum Steps {
  List = 'list',
  Detail = 'detail',
  Refuse = 'refuse',
  Error = 'error'
}

@Component({
  components: {
    PortfolioSuggestedList,
    PortfolioSuggestedDetails,
    InvestmentSuggestedDetails,
    PortfolioSuggestedRefuse,
    BaseModal,
    NavBar,
    BackButton,
    CloseButton,
    Notification,
    SuggestedValidationError,
    Portal
  }
})
export default class PortfolioSuggestedModal extends Vue {
  private currentStep = Steps.List;
  Steps = Steps;

  @CustomerModule.Getter('getCustomer')
  private customer?: ICustomer;

  @Prop({ type: Boolean, default: false })
  isOpen!: boolean;

  @Emit()
  close() {
    this.trackingCloseBtnDetail();
    this.trackingInvestmentSuggestion();
    this.currentStep = Steps.List;
  }

  private trackingCloseBtnDetail() {
    if (this.suggestionTypeInvestment) {
      trackingCloseBtnStepDetail();
    }
  }

  private trackingBackBtnDetail() {
    if (this.suggestionType === SuggestionType.Investment) {
      trackingBackBtnStepDetail();
    } else if (this.suggestionType === SuggestionType.Redeem) {
      trackViewProductDetails();
    }
  }

  private get suggestionTypeInvestment() {
    return (
      this.suggestionType === SuggestionType.Investment &&
      this.currentStep === Steps.Detail
    );
  }

  private trackingInvestmentSuggestion() {
    if (
      this.suggestionType === SuggestionType.Investment &&
      this.currentStep === Steps.List
    ) {
      closeButtonWalletDetail();
    }
  }

  setCurrentSuggestionDetails(data: { step: Steps }) {
    this.onModalNavigate(data.step);
  }

  @Emit()
  onModalNavigate(step: Steps) {
    this.trackingBackBtnDetail();
    this.currentStep = step;
  }

  created() {
    SuggestionModule.fetchSuggestions(this.customer!._id);
    toggleBodyScroll();
  }

  public get getDetailsComponent() {
    return this.suggestionType === SuggestionType.Wallet
      ? 'PortfolioSuggestedDetails'
      : 'InvestmentSuggestedDetails';
  }

  get getCustomModalHeight() {
    const customHeightConfig = {
      [Steps.List]: 520,
      [Steps.Detail]: 520,
      [Steps.Refuse]: 220,
      [Steps.Error]: 300
    };
    return customHeightConfig[this.currentStep];
  }

  get navbarTitle() {
    switch (this.currentStep) {
      case Steps.Error:
        return SuggestionModule.validationError.title;
      case Steps.Detail:
        return this.suggestionDetailNavTitle;
      default:
        return this.suggestionNavTitle;
    }
  }

  get suggestionNavTitle() {
    switch (this.suggestionType) {
      case SuggestionType.Investment:
        return 'Sugestões de investimento';
      case SuggestionType.Redeem:
        return 'Sugestões de resgate';
      default:
        return 'Sugestões de carteira';
    }
  }

  get suggestionDetailNavTitle() {
    switch (this.suggestionType) {
      case SuggestionType.Investment:
        return 'Sugestão de investimento';
      case SuggestionType.Redeem:
        return 'Sugestão de resgate';
      default:
        return this.suggestionName;
    }
  }

  get suggestionType() {
    return SuggestionModule.suggestionType;
  }

  get suggestionName() {
    return SuggestionModule.suggestionDetails.name || '';
  }

  get suggestionId() {
    return SuggestionModule.suggestionDetails.id || '';
  }

  get error() {
    return SuggestionModule.error;
  }

  get errorDescription() {
    return SuggestionModule.validationError.message;
  }

  @Emit('notify-user')
  onNotifyUser(options: NotificationOptions) {
    this.$notify(options);
  }

  private destroyed() {
    addBodyScroll();
  }
}
